/**
 * Phase 2 — ARM PROMOTE + Emergent Deployment ID capture (iter84.56.7,
 * split out of RunbookPhaseCards.jsx at iter86.83).
 */
import { useState } from "react";
import { Zap, Send, Save, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function ArmPromoteCard({ promotion, onUpdated, environment }) {
    const [busy, setBusy] = useState(false);
    const isProd = environment === "production";

    if (isProd) return null; // production never arms — promotions originate from preview

    const noPromotion = !promotion;
    const isTerminal = promotion?.status === "finalized" || promotion?.status === "cancelled";
    const armed = !!promotion?.armed_at;
    const preReady = !!promotion?.armed_for_promotion;

    const arm = async () => {
        if (!promotion) return;
        if (!preReady) {
            toast.error("Required pre-deploy gates still pending");
            return;
        }
        setBusy(true);
        try {
            const r = await api.runbookArm(promotion.id);
            toast.success("Armed — deploy from Emergent now, then tick post-deploy gates.");
            onUpdated?.(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Arm failed");
        } finally {
            setBusy(false);
        }
    };

    const phaseTw = armed
        ? "border-2 border-volt/40 bg-volt/[0.03]"
        : preReady && !noPromotion
            ? "border-2 border-cyber/40 bg-cyber/[0.03]"
            : "border-2 border-zinc-800";

    return (
        <div className={`brutal-card p-3 space-y-3 ${phaseTw}`} data-testid="phase-2-arm-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Zap className={`w-5 h-5 ${armed ? "text-volt" : preReady ? "text-cyber" : "text-zinc-500"}`} />
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                            Phase 2 · Arm + Deploy
                        </p>
                        <p className="text-xs text-zinc-200 font-bold">
                            {armed
                                ? "Armed — click Deploy in Emergent's Manage Deployments"
                                : preReady && !noPromotion
                                    ? "Pre-deploy gates clear — ready to ARM"
                                    : "Pre-deploy gates still pending — finish Phase 1 first"}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={arm}
                    disabled={busy || armed || !preReady || noPromotion || isTerminal}
                    data-testid="phase-2-arm-btn"
                    title={
                        noPromotion
                            ? "Start a promotion in Phase 0 first"
                            : !preReady
                                ? "All required pre-deploy gates must be Approved or Waived"
                                : armed
                                    ? "Already armed"
                                    : "Record that pre-deploy is clear and you're about to push Deploy"
                    }
                    className={`brutal-btn text-xs py-2 px-3 disabled:opacity-50 ${
                        armed ? "" : preReady ? "border-cyber text-cyber" : ""
                    }`}
                >
                    <Zap className="w-3 h-3" />
                    {armed ? "Armed ✓" : "ARM PROMOTE"}
                </button>
            </div>
            {armed && !isTerminal && (
                <EmergentDeploymentIdRow promotion={promotion} onUpdated={onUpdated} />
            )}
            {!armed && !noPromotion && !preReady && (
                <p className="text-[11px] text-zinc-400 italic">
                    {promotion.summary?.pre_deploy?.required_pending || 0} required
                    pre-deploy gate(s) still pending. Approve or Waive them on the cards
                    above, then come back here.
                </p>
            )}
        </div>
    );
}

function EmergentDeploymentIdRow({ promotion, onUpdated }) {
    const captured = promotion.emergent_deployment_id;
    const waived = promotion.emergent_deployment_id_waived;
    const [editing, setEditing] = useState(!captured && !waived);
    const [waiving, setWaiving] = useState(false);
    const [value, setValue] = useState(captured || "");
    const [waiverReason, setWaiverReason] = useState(
        promotion.emergent_deployment_id_waiver_reason || ""
    );
    const [saving, setSaving] = useState(false);

    const refresh = async () => {
        try {
            const fresh = await api.runbookGetPromotion(promotion.id);
            onUpdated?.(fresh);
        } catch {
            /* silent */
        }
    };

    const save = async () => {
        if (!value.trim()) {
            toast.error("Paste the deployment ID from Manage Deployments");
            return;
        }
        setSaving(true);
        try {
            await api.runbookSetEmergentDeploymentId(promotion.id, {
                deployment_id: value.trim(),
            });
            toast.success("Emergent deployment ID captured");
            setEditing(false);
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const waive = async () => {
        if (!waiverReason.trim()) {
            toast.error("Reason required to waive");
            return;
        }
        setSaving(true);
        try {
            await api.runbookSetEmergentDeploymentId(promotion.id, {
                waive: true,
                waiver_reason: waiverReason.trim(),
            });
            toast.success("Deployment ID waived");
            setWaiving(false);
            setEditing(false);
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Waive failed");
        } finally {
            setSaving(false);
        }
    };

    if (!editing && !waiving) {
        const status = captured
            ? {
                icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />,
                text: captured,
                tw: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200",
            }
            : waived
                ? {
                    icon: <AlertTriangle className="w-4 h-4 text-amber-400" />,
                    text: "Waived",
                    tw: "border-amber-500/40 bg-amber-500/5 text-amber-200",
                }
                : null;
        if (!status) return null;
        return (
            <div
                data-testid="emergent-deployment-id-display"
                data-status={captured ? "captured" : "waived"}
                className={`px-2 py-1.5 border rounded text-[11px] flex items-center gap-2 ${status.tw}`}
            >
                {status.icon}
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-75">
                        Emergent deployment ID
                    </p>
                    <p className="font-mono text-[11px] truncate" title={status.text}>
                        {status.text}
                    </p>
                    {waived && promotion.emergent_deployment_id_waiver_reason && (
                        <p
                            className="text-[10px] italic opacity-75 truncate"
                            title={promotion.emergent_deployment_id_waiver_reason}
                        >
                            "{promotion.emergent_deployment_id_waiver_reason}"
                        </p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setValue(captured || "");
                        setEditing(true);
                    }}
                    disabled={saving}
                    data-testid="emergent-deployment-id-edit-btn"
                    className="text-[10px] underline opacity-75 hover:opacity-100 shrink-0"
                >
                    Edit
                </button>
            </div>
        );
    }

    if (waiving) {
        return (
            <div
                data-testid="emergent-deployment-id-waiving"
                className="px-2 py-2 border border-amber-500/40 bg-amber-500/5 rounded text-[11px]"
            >
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-1">
                            Waive deployment ID capture
                        </p>
                        <input
                            type="text"
                            value={waiverReason}
                            onChange={(e) => setWaiverReason(e.target.value)}
                            placeholder="Why are you skipping this? (e.g. deploy rolled back, capturing later)"
                            data-testid="emergent-deployment-id-waiver-reason"
                            className="brutal-input w-full text-[11px]"
                            autoFocus
                        />
                        <div className="flex items-center gap-2 mt-2">
                            <button
                                type="button"
                                onClick={waive}
                                disabled={saving || !waiverReason.trim()}
                                data-testid="emergent-deployment-id-waive-confirm"
                                className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                            >
                                {saving ? "Waiving…" : "Confirm waive"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setWaiving(false)}
                                disabled={saving}
                                data-testid="emergent-deployment-id-waive-cancel"
                                className="text-[10px] text-zinc-400 hover:text-white underline"
                            >
                                Back
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="emergent-deployment-id-editing"
            className="px-2 py-2 border border-volt/40 bg-volt/5 rounded text-[11px]"
        >
            <div className="flex items-start gap-2">
                <Send className="w-4 h-4 text-volt mt-0.5 shrink-0" />
                <div className="flex-1">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-volt mb-1">
                        Paste Emergent deployment ID
                    </p>
                    <p className="text-[10px] text-zinc-400 mb-2 leading-relaxed">
                        Copy from Manage Deployments after clicking Deploy. Pairs the GitHub SHA
                        with the actual deployment that shipped — required (or waived) before
                        finalize.
                    </p>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="dep_abc123… (or similar)"
                        data-testid="emergent-deployment-id-input"
                        className="brutal-input w-full text-[11px] font-mono"
                        autoFocus
                    />
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving || !value.trim()}
                            data-testid="emergent-deployment-id-save-btn"
                            className="brutal-btn text-[10px] py-1 px-2 disabled:opacity-50"
                        >
                            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save"}
                        </button>
                        {(captured || waived) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setEditing(false);
                                    setValue(captured || "");
                                }}
                                disabled={saving}
                                data-testid="emergent-deployment-id-cancel-edit"
                                className="text-[10px] text-zinc-400 hover:text-white underline"
                            >
                                Cancel
                            </button>
                        )}
                        <span className="text-zinc-600 text-[10px]">·</span>
                        <button
                            type="button"
                            onClick={() => setWaiving(true)}
                            disabled={saving}
                            data-testid="emergent-deployment-id-waive-btn"
                            className="text-[10px] text-amber-300 hover:text-amber-200 underline"
                        >
                            Waive (skip with reason)
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
